/**
 * Iconos del Programa guiado (diseño v8). Portados de `program-data.jsx` del
 * prototipo. SVG inline, tamaño/color por props. Mantenidos acá para que la
 * pantalla Programa sea autocontenida.
 */
interface IconProps {
  size?: number;
  color?: string;
}

export function PrIconCap({ size = 22, color = 'currentColor' }: IconProps) {
  return (
    <svg
      viewBox="0 0 24 24"
      width={size}
      height={size}
      fill="none"
      stroke={color}
      strokeWidth="1.7"
      strokeLinejoin="round"
      strokeLinecap="round"
    >
      <path d="M2.5 9 L 12 4.5 L 21.5 9 L 12 13.5 Z" />
      <path d="M6.5 11 V 16 c 0 1.3 2.8 2.8 5.5 2.8 s 5.5 -1.5 5.5 -2.8 V 11" />
      <path d="M21.5 9 V 14" />
    </svg>
  );
}

export function PrIconCheck({ size = 14, color = 'currentColor' }: IconProps) {
  return (
    <svg
      viewBox="0 0 24 24"
      width={size}
      height={size}
      fill="none"
      stroke={color}
      strokeWidth="2.8"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M4 12 L 10 18 L 20 6" />
    </svg>
  );
}

export function PrIconClose({ size = 18, color = 'currentColor' }: IconProps) {
  return (
    <svg viewBox="0 0 24 24" width={size} height={size} fill="none" stroke={color} strokeWidth="2" strokeLinecap="round">
      <path d="M6 6 L 18 18 M18 6 L 6 18" />
    </svg>
  );
}

export function PrIconBack({ size = 20, color = 'currentColor' }: IconProps) {
  return (
    <svg
      viewBox="0 0 24 24"
      width={size}
      height={size}
      fill="none"
      stroke={color}
      strokeWidth="2.2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M15 6 L 9 12 L 15 18" />
    </svg>
  );
}

export function PrIconChevR({ size = 16, color = 'currentColor' }: IconProps) {
  return (
    <svg
      viewBox="0 0 24 24"
      width={size}
      height={size}
      fill="none"
      stroke={color}
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M9 6 L 15 12 L 9 18" />
    </svg>
  );
}

export function PrIconSpark({ size = 14, color = 'currentColor' }: IconProps) {
  return (
    <svg viewBox="0 0 24 24" width={size} height={size} fill={color} stroke="none">
      <path d="M12 2 L 13.5 9.5 L 21 11 L 13.5 12.5 L 12 20 L 10.5 12.5 L 3 11 L 10.5 9.5 Z" />
    </svg>
  );
}

export function PrIconWave({ size = 16, color = 'currentColor' }: IconProps) {
  return (
    <svg
      viewBox="0 0 24 16"
      width={size}
      height={(size * 16) / 24}
      fill="none"
      stroke={color}
      strokeWidth="2"
      strokeLinecap="round"
    >
      <line x1="2" y1="8" x2="2" y2="8" />
      <line x1="5" y1="4" x2="5" y2="12" />
      <line x1="8" y1="6" x2="8" y2="10" />
      <line x1="11" y1="2" x2="11" y2="14" />
      <line x1="14" y1="5" x2="14" y2="11" />
      <line x1="17" y1="3" x2="17" y2="13" />
      <line x1="20" y1="7" x2="20" y2="9" />
      <line x1="23" y1="8" x2="23" y2="8" />
    </svg>
  );
}

export function PrIconLayers({ size = 16, color = 'currentColor' }: IconProps) {
  return (
    <svg
      viewBox="0 0 24 24"
      width={size}
      height={size}
      fill="none"
      stroke={color}
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M12 3 L 21 8 L 12 13 L 3 8 Z" />
      <path d="M3 12 L 12 17 L 21 12" />
      <path d="M3 16 L 12 21 L 21 16" />
    </svg>
  );
}

export function PrIconList({ size = 16, color = 'currentColor' }: IconProps) {
  return (
    <svg
      viewBox="0 0 24 24"
      width={size}
      height={size}
      fill="none"
      stroke={color}
      strokeWidth="1.9"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M8 6 H 20 M 8 12 H 20 M 8 18 H 20" />
      <circle cx="4" cy="6" r="1" fill={color} stroke="none" />
      <circle cx="4" cy="12" r="1" fill={color} stroke="none" />
      <circle cx="4" cy="18" r="1" fill={color} stroke="none" />
    </svg>
  );
}

export function PrIconTarget({ size = 11, color = 'var(--text-faint)' }: IconProps) {
  return (
    <svg
      viewBox="0 0 24 24"
      width={size}
      height={size}
      fill="none"
      stroke={color}
      strokeWidth="1.9"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <circle cx="12" cy="12" r="8.5" />
      <circle cx="12" cy="12" r="4" />
      <circle cx="12" cy="12" r="1" fill={color} stroke="none" />
    </svg>
  );
}

export type ProgramGlyphDim = 'grammar' | 'vocab' | 'functional';

export function PrDimGlyph({
  d,
  size = 18,
  color = 'currentColor',
}: {
  d: ProgramGlyphDim;
  size?: number;
  color?: string;
}) {
  const common = {
    width: size,
    height: size,
    fill: 'none',
    stroke: color,
    strokeWidth: 1.8,
    strokeLinecap: 'round' as const,
    strokeLinejoin: 'round' as const,
    viewBox: '0 0 24 24',
  };
  if (d === 'grammar')
    return (
      <svg {...common}>
        <path d="M5 19 V 6 a 1 1 0 0 1 1 -1 h 8 l 4 4 v 10 a 1 1 0 0 1 -1 1 Z" />
        <path d="M13 5 v 4 h 4" />
        <path d="M8 13 h 7 M 8 16 h 5" />
      </svg>
    );
  if (d === 'vocab')
    return (
      <svg {...common}>
        <rect x="3" y="6" width="13" height="14" rx="2" />
        <path d="M8 3 h 11 a 2 2 0 0 1 2 2 v 12" />
        <path d="M6.5 11 h 6 M 6.5 14.5 h 4" />
      </svg>
    );
  return (
    <svg {...common}>
      <path d="M21 12 a 9 9 0 0 1 -13 8 L 3 21 L 4 17 A 9 9 0 1 1 21 12 Z" />
      <path d="M8.5 12 h 7 M 8.5 9 h 7" />
    </svg>
  );
}

/** Anillo de progreso compacto (sin etiqueta interna) para las theme rows. */
export function ProgramRing({
  value,
  total,
  color = 'var(--lime)',
  size = 40,
  stroke = 4,
}: {
  value: number;
  total: number;
  color?: string;
  size?: number;
  stroke?: number;
}) {
  const r = (size - stroke) / 2;
  const c = 2 * Math.PI * r;
  const pct = total > 0 ? Math.min(value / total, 1) : 0;
  return (
    <svg width={size} height={size} style={{ display: 'block', flexShrink: 0 }}>
      <circle cx={size / 2} cy={size / 2} r={r} stroke="rgba(255,255,255,0.08)" strokeWidth={stroke} fill="none" />
      <circle
        cx={size / 2}
        cy={size / 2}
        r={r}
        stroke={color}
        strokeWidth={stroke}
        fill="none"
        strokeLinecap="round"
        strokeDasharray={c}
        strokeDashoffset={c * (1 - pct)}
        transform={`rotate(-90 ${size / 2} ${size / 2})`}
      />
    </svg>
  );
}
